import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  CompositeDecorator,
  ContentBlock,
  Editor,
  EditorState,
  RichUtils,
  convertFromRaw,
  convertToRaw,
  getDefaultKeyBinding,
} from "draft-js";
import {
  blockRenderMap,
  changeDepth,
  extractInlineStyle,
  getCustomStyleMap,
  getSelectedBlocksType,
  handleNewLine,
} from "draftjs-utils";
import { Component } from "react";

// draft js utils
import defaultToolbar from "./config/defaultToolbar";
import Controls from "./controls";
import getLinkDecorator from "./decorators/Link";
import getPropertyDecorator from "./decorators/Property";
import FocusHandler from "./event-handler/focus";
import getBlockRenderFunc from "./renderer";
import blockStyleFn from "./utils/BlockStyle";
import { hasProperty } from "./utils/common";

// components
import Icon from "../icon";
import UploadSection from "./upload";

// styles
import {
  EditorInnerWrapperStyled,
  EditorMainWrapperStyled,
  UploadSectionCloseButtonStyled,
} from "./editor.styled";

interface Props {
  editorState: any;
  onBlur?: any;
  onFocus?: any;
  onTab?: any;
  readOnly?: any;
  onEditorStateChange: any;
  onChange?: any;
  editorRef?: any;
  mediaInfo?: MediaInfoType;
  whatsappEditorOptions?: boolean;
  hideFocusOnLoad?: boolean;
  error?: boolean;
  media?: boolean;
  maxTextLimit?: number;
  disableApi?: boolean;
  onError?: (error: string | null) => void;
  isMediaAndLinkDisabled?: any
  isFileUploadDisabled?: any;
  disableBullets?: boolean;
  disableUnderline?: boolean;
}

type URL = {
  url: string;
};

type MediaInfoType = {
  file: URL;
  image: URL;
  video: URL;
};

interface States {
  editorState: any;
  editorFocused: boolean;
  toolbar: any;
  upload: string;
  mediaInfo: MediaInfoType;
  error: boolean;
  internalError: string | null;
}

const defaultMediaInfo = {
  file: { url: "" },
  image: { url: "" },
  video: { url: "" },
};

class UIEditor extends Component<Props, States> {
  blockRendererFn: ((block: ContentBlock) => any) | undefined;
  focusHandler: any;
  compositeDecorator: CompositeDecorator;
  wrapper: any;
  editor: any;

  constructor(props: any) {
    super(props);
    const toolbar = defaultToolbar;
    this.focusHandler = new FocusHandler();
    this.blockRendererFn = getBlockRenderFunc(
      {
        getEditorState: this.getEditorState,
        onChange: this.onChange,
      },
      props.customBlockRenderFunc
    );
    this.compositeDecorator = this.getCompositeDecorator(toolbar);
    const editorState = this.createEditorState(this.compositeDecorator);
    extractInlineStyle(editorState);
    this.state = {
      editorState,
      editorFocused: false,
      toolbar,
      upload: props?.mediaInfo ? this.getuploadType() : "",
      mediaInfo: props?.mediaInfo ? props.mediaInfo : defaultMediaInfo,
      error: props?.error || false,
      internalError: null,
    };
  }

  getuploadType = () => {
    const { mediaInfo } = this.props;
    let uploadType = "";
    if (mediaInfo) {
      Object?.entries(mediaInfo)?.map((item) => {
        if (item[1]?.url !== "") {
          uploadType = item[0];
        }
      });
    }
    return uploadType;
  };

  focusEditor = () => {
    setTimeout(() => {
      if (this.editor !== null)
        this.editor.focus();
    });
  };

  componentDidMount() {
    // * kindly do not remove to stop focus mode onLoad
    if (this.props.hideFocusOnLoad) {
      //do nothing 
    }
    else {
      this.focusEditor();
    }
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps === this.props) return;
    const newState: any = {};
    const { editorState, mediaInfo } = this.props;
    if (
      hasProperty(this.props, "editorState") &&
      editorState !== prevProps.editorState
    ) {
      if (editorState) {
        newState.editorState = EditorState.set(editorState, {
          decorator: this.compositeDecorator,
        });
      } else {
        newState.editorState = EditorState.createEmpty(this.compositeDecorator);
      }
    }
    if (prevProps.editorState !== editorState) {
      extractInlineStyle(newState.editorState);
    }
    if (Object.keys(newState).length) this.setState(newState);

    //* Fixed: sometimes mediaInfo wasn't updating while retriving values from api 
    if (mediaInfo && hasProperty(this.props, "mediaInfo") && prevProps?.mediaInfo !== mediaInfo) {
      this.setState({ mediaInfo, upload: this.getuploadType() })
    }
  }

  getCompositeDecorator = (toolbar: any) => {
    const decorators = [
      getLinkDecorator({
        showOpenOptionOnHover: toolbar.link.showOpenOptionOnHover,
      }),
      getPropertyDecorator({}),
    ];
    return new CompositeDecorator(decorators);
  };

  createEditorState = (compositeDecorator: any) => {
    let editorState;
    if (hasProperty(this.props, "editorState")) {
      if (this.props.editorState) {
        editorState = EditorState.set(this.props.editorState, {
          decorator: compositeDecorator,
        });
      }
    }

    if (!editorState) {
      editorState = EditorState.createEmpty(compositeDecorator);
    }
    return editorState;
  };

  getEditorState = () => (this.state ? this.state.editorState : null);

  changeEditorState = (contentState: any) => {
    const newContentState = convertFromRaw(contentState);
    let { editorState } = this.state;
    editorState = EditorState.push(
      editorState,
      newContentState,
      "insert-characters"
    );
    editorState = EditorState.moveSelectionToEnd(editorState);
    return editorState;
  };

  setEditorReference = (ref: any) => {
    if (this.props.editorRef) {
      this.props.editorRef(ref);
    }
    this.editor = ref;
  };

  keyBindingFn = (event: any) => {
    if (event.key === "Tab") {
      const { onTab } = this.props;
      if (!onTab || !onTab(event)) {
        const editorState = changeDepth(
          this.state.editorState,
          event.shiftKey ? -1 : 1,
          4
        );
        if (editorState && editorState !== this.state.editorState) {
          this.onChange(editorState);
          event.preventDefault();
        }
      }
      return null;
    }

    return getDefaultKeyBinding(event);
  };

  onChange = (editorState: any) => {
    const { readOnly, onEditorStateChange, mediaInfo, maxTextLimit, onError } = this.props;
    
    // Character limit validation
    if (maxTextLimit && maxTextLimit > 0) {
      const charLength = this.getPlainTextLength(editorState);
      const currentCharLength = this.getPlainTextLength(this.state.editorState);
      
      // More strict validation: prevent any change that would exceed the limit
      if (charLength > maxTextLimit) {
        // Only allow if we're deleting (charLength <= currentCharLength)
        if (charLength > currentCharLength) {
          // Don't update editorState if trying to add characters beyond maxTextLimit
          const errorMessage = `Character limit of ${maxTextLimit} exceeded`;
          this.setState({ internalError: errorMessage });
          if (onError) {
            onError(errorMessage);
          }
          return;
        }
      }
      
      // Clear error if within limit or valid operation
      if (charLength <= maxTextLimit || charLength <= currentCharLength) {
        this.setState({ internalError: null });
        if (onError) {
          onError(null);
        }
      }
    }
    
    if (
      !readOnly &&
      !(
        getSelectedBlocksType(editorState) === "atomic" &&
        editorState.getSelection().isCollapsed
      )
    ) {
      if (onEditorStateChange) {
        onEditorStateChange(
          editorState,
          mediaInfo
        );
      }
      if (!hasProperty(this.props, "editorState")) {
        // @ts-ignore
        this.setState({ editorState }, this.afterChange(editorState));
      } else {
        this.afterChange(editorState);
      }
    }
  };

  afterChange = (editorState: any) => {
    setTimeout(() => {
      const { onChange } = this.props;
      if (onChange) {
        onChange(
          convertToRaw(editorState.getCurrentContent()),
          this.state.mediaInfo
        );
      }
    });
  };

  handleReturn = (event: any) => {
    const { editorState } = this.state;
    const newEditorState = handleNewLine(editorState, event);
    if (newEditorState) {
      this.onChange(newEditorState);
      return true;
    }
    return false;
  };

  handleKeyCommand = (command: any) => {
    const {
      editorState,
      toolbar: { inline },
    } = this.state;
    if (inline && inline.options.indexOf(command) >= 0) {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        this.onChange(newState);
      }
    }
  };

  handleBeforeInput = (chars: string, editorState: any) => {
    const { maxTextLimit, onError } = this.props;
    
    if (maxTextLimit && maxTextLimit > 0) {
      const currentLength = this.getPlainTextLength(editorState);
      const newLength = currentLength + chars.length;
      
      if (newLength > maxTextLimit) {
        // Set error message when preventing input
        const errorMessage = `Character limit of ${maxTextLimit} exceeded`;
        this.setState({ internalError: errorMessage });
        if (onError) {
          onError(errorMessage);
        }
        // Prevent the input
        return 'handled';
      } else {
        // Clear error if input is allowed
        this.setState({ internalError: null });
        if (onError) {
          onError(null);
        }
      }
    }
    
    return 'not-handled';
  };

  preventDefault = (event: any) => {
    if (event.target.tagName === "INPUT") {
      this.focusHandler.onInputMouseDown();
    } else {
      event.preventDefault();
    }
  };

  onToolbarFocus = (event: any) => {
    const { onFocus } = this.props;
    if (onFocus && this.focusHandler.isToolbarFocused()) {
      onFocus(event);
    }
  };

  onUploadClick = (uploadOption: string) => {
    this.setState({ upload: uploadOption });
  };

  onMediaSave = (url: string, type: string) => {
    const { onEditorStateChange } = this.props;
    this.setState({
      mediaInfo: { ...defaultMediaInfo, [type]: { url } },
    }, () => {
      if (onEditorStateChange) {
        onEditorStateChange(
          this.state.editorState,
          { ...defaultMediaInfo, ...{ [type]: { url } } }
        );
      }
    });
  };

  onMediaDelete = () => {
    const { onEditorStateChange } = this.props;
    this.setState({
      mediaInfo: {
        ...defaultMediaInfo,
        ...{ [this.state.upload]: { url: "" } },
      },
    });
    if (onEditorStateChange) {
      onEditorStateChange(
        this.state.editorState,
        { ...defaultMediaInfo, ...{ [this.state.upload]: { url: "" } } }
      );
    }
  };

  getStyleMap = () => ({ ...getCustomStyleMap() });

  getTextData = () => {
    const { editorState } = this.state;
    let value = "";
    if (editorState) {
      const blocks = convertToRaw(
        this.state.editorState.getCurrentContent()
      ).blocks;
      value = blocks
        .map((block) => (!block.text.trim() && "\n") || block.text)
        .join("\n");
    }
    return value;
  };

  getPlainTextLength = (editorState: any) => {
    if (!editorState) return 0;
    const blocks = convertToRaw(editorState.getCurrentContent()).blocks;
    const plainText = blocks
      .map((block) => block.text)
      .join("\n");
    return plainText.length;
  };

  handleBlur = () => {
    const { onBlur } = this.props;
    if (onBlur) {
      const blocks = convertToRaw(
        this.state.editorState.getCurrentContent()
      ).blocks;
      const value = blocks
        .map((block) => (!block.text.trim() && "\n") || block.text)
        .join("\n");
      onBlur(value);
    }
  };

  render() {
    const { editorState, toolbar, upload, mediaInfo, internalError } = this.state;
    const { maxTextLimit } = this.props;
    const controlProps = {
      editorState,
      onChange: this.onChange,
      onUploadClick: this.onUploadClick,
    };

    let checkForURL: any = upload !== "" ? mediaInfo : "";
    // @ts-ignore
    checkForURL = checkForURL[upload];

    let isURL = checkForURL?.url;
    let uploadURLType = upload;
    Object.entries(mediaInfo).map((item) => {
      if (item[1]?.url !== "" && item[0] === upload) {
        isURL = item[1]?.url;
        uploadURLType = item[0];
      }
    });

    return (
      <Box>
      <EditorMainWrapperStyled
        error={this.props.error || false}
        onClick={(e) => {
          e.stopPropagation();
          this.focusEditor();
        }}
      >
        {(isURL || uploadURLType) && (
          <Box sx={{ position: "relative" }}>
            {!isURL && (
              <UploadSectionCloseButtonStyled
                onClick={() => this.setState({ upload: "" })}
                size="medium"
              >
                <Icon icon="close" size={12} color="#fff" />
              </UploadSectionCloseButtonStyled>
            )}
            <UploadSection
              url={isURL}
              type={uploadURLType}
              onSave={this.onMediaSave}
              onDelete={this.onMediaDelete}
              disableDeleteMedia={this.props.readOnly}
              isFileUploadDisabled={this.props.isFileUploadDisabled}
            />
          </Box>
        )}
        <EditorInnerWrapperStyled>
          <Editor
            ref={this.setEditorReference}
            keyBindingFn={this.keyBindingFn}
            editorState={editorState}
            onChange={this.onChange}
            blockStyleFn={blockStyleFn}
            customStyleMap={this.getStyleMap()}
            blockRendererFn={this.blockRendererFn}
            // @ts-ignore
            handleKeyCommand={this.handleKeyCommand}
            handleBeforeInput={this.handleBeforeInput}
            blockRenderMap={blockRenderMap}
            onBlur={this.handleBlur}
            placeholder="Type your message here"
            readOnly={this.props.readOnly}
          />
        </EditorInnerWrapperStyled>

      </EditorMainWrapperStyled>
      <Box onMouseDown={this.preventDefault} onFocus={this.onToolbarFocus} sx={{padding: "6px",  background: "#f5f6f8", width: "100%"}} >

          <Stack direction={"row"} spacing={2}>
            {toolbar.options.map((opt: any, index: any) => {
              // Handle global disabling of bullet options
              const disableBullets = this.props?.disableBullets;
              const disableUnderline = this.props?.disableUnderline;

              if (typeof this.props?.media === 'boolean') {
                if (!this.props?.media && opt === 'upload') {
                  return;
                }
              }
              // @ts-ignore
              const Control = Controls[opt];
              const config = toolbar[opt];

              if(this.props.isMediaAndLinkDisabled && (opt === "link" || opt === "upload")){
                return null
              }

              if(this.props?.disableApi && opt === "api"){
                return null;
              }

              //* hiding some options for whatsapp editor
              if (
                this.props.readOnly ||
                (this.props.whatsappEditorOptions && (config?.icon === "upload" || config?.link?.icon === "link")) ||
                (this.props.whatsappEditorOptions === undefined && (opt === "upload" || opt === "link"))
              ) {
                return null;
              }
              if (this.props.whatsappEditorOptions && !this.props.readOnly) {
                config['blockOptions'] = ['unordered', 'ordered'];
                // Ensure underline is included in the options
                if (config['options'] && !config['options'].includes('underline')) {
                  config['options'].push('underline');
                }
              }
              // Disable bullets and underline if specified
              if ((disableBullets || disableUnderline) && opt === 'inline' && config) {
                if (disableBullets) {
                  if (config.blockOptions) {
                    config.blockOptions = config.blockOptions.filter((b: string) => !['unordered', 'ordered'].includes(b));
                  }
                  if (config.options) {
                    config.options = config.options.filter((b: string) => !['unordered', 'ordered'].includes(b));
                  }
                }
                if (disableUnderline && config.options) {
                  config.options = config.options.filter((b: string) => b !== 'underline');
                }
              }

              return <Control key={index} {...controlProps} config={config} />;
            })}
          </Stack>
        </Box>
      </Box>
    );
  }
}

export default UIEditor;
